// @flow

import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk';

import type { AuthState } from './auth';
import auth from './auth';
import type { CompanyState } from './company';
import company from './company';
import type { ContractorState } from './contractor';
import contractor from './contractor';
import type { ContractorsState } from './contractors';
import contractors from './contractors';
import type { ContractVehicleState } from './contractVehicle';
import contractVehicle from './contractVehicle';
import contractVehicleOrder, {
  type ContractVehicleOrderState
} from './contractVehicleOrder';
import type { EmployeeState } from './employee';
import employee from './employee';
import type { FuelAndOilState } from './fuelAndOil';
import fuelAndOil from './fuelAndOil';
import type { FuelMultiplierState } from './fuelMultiplier';
import fuelMultiplier from './fuelMultiplier';
import type { FuelsAndOilsState } from './fuelsAndOils';
import fuelsAndOils from './fuelsAndOils';
import type { JournalState } from './journal';
import journal from './journal';
import type { JournalsState } from './journals';
import journals from './journals';
import type { MaintenanceState } from './maintenance';
import maintenance from './maintenance';
import maintenanceOperation from './maintenanceOperation';
import maintenanceOperationGroup from './maintenanceOperationGroup';
import maintenanceOperationGroups from './maintenanceOperationGroups';
import type { MaintenancesState } from './maintenances';
import maintenances from './maintenances';
import { default as mdmNode, type MdmNodeState } from './mdmNode';
import type { MonitoringState } from './monitoring';
import monitoring from './monitoring';
import type { OrderCalculationState, OrderState } from './order';
import order from './order';
import orgUnits from './orgUnits';
import type { RepairOrderState } from './repairOrder';
import repairOrder from './repairOrder';
import type { RoleState } from './role';
import role from './role';
import type { RolesState } from './roles';
import roles from './roles';
import type { ShiftState } from './shift';
import shift from './shift';
import type { SidebarState } from './sidebar';
import sidebar from './sidebar';
import type { ThirdPartyEmployeeState } from './thirdPartyEmployee';
import thirdPartyEmployee from './thirdPartyEmployee';
import type { TripState } from './trip';
import trip from './trip';
import type { VehicleState } from './vehicle';
import vehicle from './vehicle';
import type { VehicleModelState } from './vehicleModel';
import vehicleModel from './vehicleModel';
import type { VehicleModelsState } from './vehicleModels';
import vehicleModels from './vehicleModels';

import type {
  MaintenanceOperation,
  MaintenanceOperationGroup,
  OrgUnitNode
} from '../lib/types';
import branchEmployeesPositions, {
  type EmployeeBranchPositionState
} from './branchEmployeesPositions';
import detailedDefectiveStatement from './detailedDefectiveStatement';
import jobTitle, { type JobTitleState } from './jobTitle';
import jobTitles, { type JobTitlesState } from './jobTitles';
import osagoCalculation, {
  type OsagoCalculationState
} from './osagoCalculation';
import vehiclePlan, { type VehiclePlanState } from './vehiclePlan';
import vehicleTaxPlan, { type VehicleTaxPlanState } from './vehicleTaxPlan';

import persistFilters, { type PersistFiltersState } from './persistFilters';

// Стейт списков
export type ListState<T> = {
  data: T[],
  page: number,
  pageSize: number,
  totalCount: number
};

export type Action = {
  type: string,
  payload: any
};

// Стейт приложения
export type AppState = {
  auth: AuthState,
  branchEmployeesPositions: EmployeeBranchPositionState,
  employee: EmployeeState,
  thirdPartyEmployee: ThirdPartyEmployeeState,
  orgUnits: OrgUnitNode,
  vehicle: VehicleState,
  order: OrderState,
  orderCalculation: OrderCalculationState,
  shift: ShiftState,
  trip: TripState,
  vehicleModel: VehicleModelState,
  vehicleModels: VehicleModelsState,
  fuelAndOil: FuelAndOilState,
  fuelsAndOils: FuelsAndOilsState,
  journals: JournalsState,
  journal: JournalState,
  company: CompanyState,
  contractor: ContractorState,
  contractors: ContractorsState,
  contractVehicle: ContractVehicleState,
  sidebar: SidebarState,
  maintenanceOperationGroups: MaintenanceOperationGroup,
  maintenanceOperationGroup: MaintenanceOperationGroup,
  maintenanceOperation: MaintenanceOperation,
  maintenance: MaintenanceState,
  maintenances: MaintenancesState,
  repairOrder: RepairOrderState,
  role: RoleState,
  roles: RolesState,
  monitoring: MonitoringState,
  contractVehicleOrder: ContractVehicleOrderState,
  mdmNode: MdmNodeState,
  fuelMultiplier: FuelMultiplierState,
  jobTitle: JobTitleState,
  jobTitles: JobTitlesState,
  osagoCalculation: OsagoCalculationState,
  persistFilters: PersistFiltersState,
  vehiclePlan: VehiclePlanState,
  vehicleTaxPlan: VehicleTaxPlanState
};

const rootReducer = combineReducers({
  auth,
  orgUnits,
  branchEmployeesPositions,
  employee,
  thirdPartyEmployee,
  vehicle,
  order,
  shift,
  trip,
  vehicleModel,
  vehicleModels,
  fuelAndOil,
  fuelsAndOils,
  journals,
  journal,
  company,
  contractor,
  contractors,
  contractVehicle,
  sidebar,
  maintenanceOperationGroups,
  maintenanceOperationGroup,
  maintenanceOperation,
  maintenance,
  maintenances,
  repairOrder,
  role,
  roles,
  monitoring,
  contractVehicleOrder,
  mdmNode,
  fuelMultiplier,
  jobTitle,
  jobTitles,
  osagoCalculation,
  vehiclePlan,
  vehicleTaxPlan,
  detailedDefectiveStatement,
  persistFilters
});

const defaultCfg = {
  key: 'primary',
  storage,
  whitelist: ['persistFilters']
};

const composeEnhancer = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

export const initStore = (persistCfg: Object = {}) => {
  const persistedReducer = persistReducer(
    Object.assign(defaultCfg, persistCfg),
    rootReducer
  );
  const store = createStore(
    persistedReducer,
    process.env.REACT_APP_IS_DEV === 'false'
      ? applyMiddleware(thunk)
      : composeEnhancer(applyMiddleware(thunk))
  );

  const persistor = persistStore(store);
  return { store, persistor };
};
